import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Search, Filter, X } from "lucide-react";
import { fetchBlogPosts, BlogFrontMatter } from "@/lib/blog";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useTranslation } from "react-i18next";

export default function BlogPage({ lang = "en" }: { lang?: string }) {
  const { t } = useTranslation();
  const [posts, setPosts] = useState<BlogFrontMatter[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [allTags, setAllTags] = useState<string[]>([]);
  const [tagMode, setTagMode] = useState<"single" | "multiple">("single");

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    async function fetchPosts() {
      setLoading(true);
      
      const allPosts = await fetchBlogPosts(lang);
      console.log("Fetched blog posts:", allPosts);
      setPosts(allPosts);
      
      // Extract unique tags from all posts
      const tagsFromPosts = allPosts.flatMap(post => post.tags || []);
      const uniqueTags = Array.from(new Set(tagsFromPosts)).sort();
      setAllTags(uniqueTags);
      
      setLoading(false);
    }
    
    fetchPosts();
  }, [lang]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "No date";
    
    try {
      const date = new Date(dateString);
      
      // Check if date is valid before formatting
      if (isNaN(date.getTime())) {
        console.error("Invalid date:", dateString);
        return "Invalid date";
      }
      
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date error";
    }
  };

  const handleTagToggle = (tag: string) => {
    if (tagMode === "single") {
      // In single mode, selecting a tag replaces any previously selected tag
      setSelectedTags(selectedTags.includes(tag) ? [] : [tag]);
    } else {
      // In multiple mode, add or remove the tag from the selection
      setSelectedTags(prevTags => {
        if (prevTags.includes(tag)) {
          return prevTags.filter(t => t !== tag);
        } else {
          return [...prevTags, tag];
        }
      });
    }
  };

  const clearAllTags = () => {
    setSelectedTags([]);
  };

  const filteredPosts = posts
    .filter(post => {
      if (selectedTags.length === 0) return true;
      
      if (tagMode === "multiple") {
        // In multiple mode, show posts that have all selected tags
        return selectedTags.every(tag => post.tags?.includes(tag) || false);
      } else {
        // In single mode, show posts that have the selected tag
        return post.tags?.some(tag => selectedTags.includes(tag)) || false;
      }
    })
    .filter(post =>
      searchQuery === "" || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
      (post.excerpt && post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()))
    );

  return (
    <div className="container py-12">
      <h1 className="text-4xl font-bold mb-8">{t("blog")}</h1>
      
      <div className="flex flex-col space-y-4 mb-8">
        {/* Search bar and filter options in one compact row */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder={t("search_placeholder")}
              className="w-full rounded-md border border-input bg-background px-3 py-2 pl-10 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          </div>
          
          {/* Compact mode selection */}
          <Tabs value={tagMode} onValueChange={(value) => {
            setTagMode(value as "single" | "multiple");
            setSelectedTags([]); // Clear selection when changing modes
          }} className="flex items-center">
            <TabsList className="h-9">
              <TabsTrigger value="single" className="text-xs px-3">{t("single_tag")}</TabsTrigger>
              <TabsTrigger value="multiple" className="text-xs px-3">{t("multiple_tag")}</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        
        {/* Tag filter - compact design */}
        {allTags.length > 0 && (
          <div className="bg-muted/50 p-2 rounded-md">
            <div className="flex flex-wrap gap-1.5">
              {allTags.map((tag) => (
                <Badge
                  key={tag}
                  variant={selectedTags.includes(tag) ? "default" : "outline"}
                  className="cursor-pointer text-xs"
                  onClick={() => handleTagToggle(tag)}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Selected tags summary */}
        {selectedTags.length > 0 && (
          <div className="flex items-center text-xs gap-2">
            <span className="text-muted-foreground">{tagMode === "single" ? t("filtered_by_tag") : t("filtered_by_tags")}</span>
            <div className="flex flex-wrap gap-1.5 items-center">
              {selectedTags.map(tag => (
                <Badge key={tag} variant="secondary" className="text-xs cursor-pointer flex items-center gap-1 py-0 px-2" onClick={() => handleTagToggle(tag)}>
                  {tag}
                  <X className="h-3 w-3 ml-1" />
                </Badge>
              ))}
            </div>
            <button
              onClick={clearAllTags}
              className="text-xs text-primary hover:underline ml-2"
            >
              {t("clear_all")}
            </button>
          </div>
        )}
      </div>
      
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : filteredPosts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-xl text-muted-foreground">{t("no_results")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredPosts.map((post) => {
            const basePath = lang === "en" ? "/blog" : `/${lang}/blog`;
            return (
              <Link
                key={post.slug}
                to={`${basePath}/${post.slug}`}
                className="group"
              >
                <div className="card-enhanced bg-card rounded-lg overflow-hidden h-full flex flex-col shadow-md hover:shadow-lg transition-shadow duration-300">
                  {post.cover && (
                    <div className="w-full h-48 overflow-hidden">
                      <img 
                        src={typeof post.cover === 'string' ? post.cover : post.cover.image || ''} 
                        alt={post.cover_alt || post.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ease-in-out" 
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-grow">
                    <h2 className="text-xl font-semibold mb-3 group-hover:text-primary transition-colors">{post.title}</h2>
                    {/* Only show excerpt if it exists and is not 'no_excerpt' (case-insensitive, trimmed) */}
                    {post.excerpt && post.excerpt.trim().toLowerCase() !== 'no_excerpt' ? (
                      <p className="text-sm text-muted-foreground mb-4 flex-grow">{post.excerpt}</p>
                    ) : null}
                    <div className="mt-auto space-y-3">
                      {/* Only show published date if it is defined and not a default/invalid value */}
                      {post.published_at &&
                        post.published_at !== '1970-01-01' &&
                        post.published_at !== '1970-01-01T00:00:00Z' &&
                        post.published_at !== 'January 1, 1970' &&
                        post.published_at.trim() !== '' && (
                        <p className="text-xs text-muted-foreground">{formatDate(post.published_at)}</p>
                      )}
                      {post.tags && post.tags.length > 0 && (
                         <div className="flex flex-wrap gap-1">
                          {post.tags.map(tag => (
                            <Badge key={`${post.slug}-${tag}`} variant="outline" className="text-xs px-2 py-0.5">
                              {tag}
                            </Badge>
                          ))}
                         </div>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
